import { useId, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/index.js';
import Icon from '../../components/Icon.jsx';
import { haptic } from '../../app/settingsStore.js';
import styles from './NoteForm.module.scss';

const SUGGESTIONS = 10;

/**
 * Tags currently on live notes.
 * Safari throws UnknownError "Unable to open cursor" for `orderBy().uniqueKeys()`
 * on the multiEntry tags index, especially while the store is empty. That failure
 * can lock IndexedDB, so the next save — creating a deck included — fails the same way.
 */
export function useAllTags() {
  return useLiveQuery(
    async () => {
      const notes = await db.notes.toArray();
      const tags = new Set();
      for (const note of notes) {
        if (note.deletedAt || note.purged) continue;
        for (const tag of note.tags ?? []) tags.add(tag);
      }
      return [...tags].sort((a, b) => a.localeCompare(b));
    },
    [],
    [],
  );
}

export default function TagInput({ value = [], onChange, label = 'Tags' }) {
  const [draft, setDraft] = useState('');
  const inputId = useId();
  const all = useAllTags();

  const add = (raw) => {
    const parts = raw
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (parts.length) onChange([...new Set([...value, ...parts])]);
    setDraft('');
  };

  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return all
      .filter((t) => !value.includes(t) && (!q || t.toLowerCase().includes(q)))
      .slice(0, SUGGESTIONS);
  }, [all, value, draft]);

  return (
    <section className={styles.fieldCard}>
      <header className={styles.fieldHead}>
        <label htmlFor={inputId} className={styles.fieldLabel}>
          {label}
          <span className={styles.optional}>Optional</span>
        </label>
        <span className={styles.fieldHint}>
          Group cards by topic so you can study them together.
        </span>
      </header>
      {value.length ? (
        <div className={styles.tagList}>
          {value.map((t) => (
            <button
              key={t}
              type="button"
              className={styles.tagChip}
              onClick={() => {
                haptic(5);
                onChange(value.filter((x) => x !== t));
              }}
              aria-label={`Remove tag ${t}`}
            >
              {t}
              <Icon name="close" size={14} stroke={2.6} />
            </button>
          ))}
        </div>
      ) : null}
      <div className={styles.tagInputRow}>
        <Icon name="tag" size={18} />
        <input
          id={inputId}
          className={styles.tagInput}
          value={draft}
          placeholder="Type a tag and press Enter"
          autoCapitalize="off"
          autoCorrect="off"
          enterKeyHint="done"
          onChange={(e) => {
            const v = e.target.value;
            if (/[\s,]$/.test(v)) add(v);
            else setDraft(v);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && draft) {
              e.preventDefault();
              add(draft);
            } else if (e.key === 'Backspace' && !draft && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => draft && add(draft)}
        />
        {draft ? (
          <button type="button" className={styles.tagAdd} onClick={() => add(draft)}>
            Add
          </button>
        ) : null}
      </div>
      {suggestions.length ? (
        <div className={styles.suggest} aria-label="Your tags">
          {suggestions.map((t) => (
            <button
              key={t}
              type="button"
              className={styles.suggestChip}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => {
                haptic(5);
                onChange([...value, t]);
                setDraft('');
              }}
            >
              <Icon name="plus" size={14} stroke={2.6} />
              {t}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
